/**
 * Locale-aware content catalogue for the admin console.
 *
 * Group exports are Proxies that read from `getActiveLocale()` on each access.
 */

import { getActiveLocale, type Locale } from "@/utils/locale";

import * as ar from "./dashboard-ar";
import * as en from "./dashboard-en";

const catalogues = { ar, en } as const;

export function catalogue(locale?: Locale): typeof ar {
  return catalogues[locale ?? getActiveLocale()] as typeof ar;
}

type Catalogue = typeof ar;
type GroupName = keyof Catalogue;

function deepProxy<T extends object>(getTarget: () => T): T {
  const cache = new Map<PropertyKey, unknown>();
  return new Proxy({} as T, {
    get(_target, prop, receiver) {
      if (typeof prop === "symbol") return Reflect.get(getTarget(), prop, receiver);
      const live = getTarget() as Record<PropertyKey, unknown>;
      const value = live[prop];
      if (value !== null && typeof value === "object" && !Array.isArray(value)) {
        let nested = cache.get(prop);
        if (!nested) {
          nested = deepProxy(
            () => (getTarget() as Record<PropertyKey, unknown>)[prop] as object,
          );
          cache.set(prop, nested);
        }
        return nested;
      }
      return value;
    },
    ownKeys() {
      return Reflect.ownKeys(getTarget());
    },
    getOwnPropertyDescriptor(_target, prop) {
      return Reflect.getOwnPropertyDescriptor(getTarget(), prop);
    },
    has(_target, prop) {
      return prop in getTarget();
    },
  });
}

function groupProxy<K extends GroupName>(name: K): Catalogue[K] {
  return deepProxy(() => catalogue()[name]);
}

export const app = groupProxy("app");
export const nav = groupProxy("nav");
export const common = groupProxy("common");
export const status = groupProxy("status");
export const roles = groupProxy("roles");
export const auth = groupProxy("auth");
export const mobileOnly = groupProxy("mobileOnly");
export const errors = groupProxy("errors");
export const overview = groupProxy("overview");
export const employees = groupProxy("employees");
export const employeeRecord = groupProxy("employeeRecord");
export const genders = groupProxy("genders");
export const maritalStatuses = groupProxy("maritalStatuses");
export const bloodTypes = groupProxy("bloodTypes");
export const studyModes = groupProxy("studyModes");
export const addressKinds = groupProxy("addressKinds");
export const contractStatuses = groupProxy("contractStatuses");
export const zones = groupProxy("zones");
export const shifts = groupProxy("shifts");
export const leaveGroups = groupProxy("leaveGroups");
export const leave = groupProxy("leave");
export const leaveTypes = groupProxy("leaveTypes");
export const breaks = groupProxy("breaks");
export const incidents = groupProxy("incidents");
export const incidentTypes = groupProxy("incidentTypes");
export const deviceChanges = groupProxy("deviceChanges");
export const attendance = groupProxy("attendance");
export const tracking = groupProxy("tracking");
export const reports = groupProxy("reports");
export const settings = groupProxy("settings");
export const notificationText = groupProxy("notificationText");
export const notifications = groupProxy("notifications");
export const audit = groupProxy("audit");
export const theme = groupProxy("theme");
export const pagination = groupProxy("pagination");
export const filters = groupProxy("filters");
export const palette = groupProxy("palette");
export const notFound = groupProxy("notFound");
export const queryState = groupProxy("queryState");
export const recordNotFound = groupProxy("recordNotFound");
export const confirm = groupProxy("confirm");
export const maintenance = groupProxy("maintenance");
export const updates = groupProxy("updates");
export const policies = groupProxy("policies");
export const companies = groupProxy("companies");
export const payrollRuns = groupProxy("payrollRuns");
export const weekdays = groupProxy("weekdays");
export const metrics = groupProxy("metrics");
export const charts = groupProxy("charts");
